import { useEffect, useState } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/schedules")({
  head: () => ({ meta: [{ title: "Jadwal Pelajaran — MI Al-Munajah Admin" }] }),
  component: SchedulesPage,
});

type Day = "senin" | "selasa" | "rabu" | "kamis" | "jumat" | "sabtu" | "minggu";
const days: Day[] = ["senin", "selasa", "rabu", "kamis", "jumat", "sabtu"];

type Schedule = {
  id: string; day: Day; start_time: string; end_time: string;
  subject: string; teacher_id: string | null; class_id: string | null;
};
type Opt = { id: string; name: string };

const empty = {
  day: "senin" as Day, start_time: "07:00", end_time: "08:00",
  subject: "", teacher_id: null as string | null, class_id: null as string | null,
};

function SchedulesPage() {
  const [rows, setRows] = useCachedState<Schedule[]>("schedules:rows", []);
  const [classes, setClasses] = useCachedState<Opt[]>("schedules:classes", []);
  const [teachers, setTeachers] = useCachedState<Opt[]>("schedules:teachers", []);
  const [filterClass, setFilterClass] = useState<string>("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Schedule | null>(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await supabase.from("schedules").select("*").order("start_time");
    setRows((data || []) as Schedule[]);
  };
  useEffect(() => {
    load();
    supabase.from("classes").select("id,name").order("name").then(({ data }) => setClasses((data || []) as Opt[]));
    supabase.from("teachers").select("id,name").order("name").then(({ data }) => setTeachers((data || []) as Opt[]));
    const ch = supabase.channel("sch-rt").on("postgres_changes", { event: "*", schema: "public", table: "schedules" }, load).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const startNew = () => { setEditing(null); setForm({ ...empty, class_id: filterClass !== "all" ? filterClass : null }); setOpen(true); };
  const startEdit = (s: Schedule) => {
    setEditing(s);
    setForm({ day: s.day, start_time: s.start_time.slice(0,5), end_time: s.end_time.slice(0,5), subject: s.subject, teacher_id: s.teacher_id, class_id: s.class_id });
    setOpen(true);
  };
  const save = async () => {
    if (!form.subject || !form.class_id) return toast.error("Mata pelajaran & kelas wajib diisi");
    const { error } = editing
      ? await supabase.from("schedules").update(form).eq("id", editing.id)
      : await supabase.from("schedules").insert(form);
    if (error) return toast.error(error.message);
    toast.success(editing ? "Jadwal diperbarui" : "Jadwal ditambahkan");
    setOpen(false);
  };
  const remove = async (id: string) => {
    const { error } = await supabase.from("schedules").delete().eq("id", id);
    if (error) toast.error(error.message); else toast.success("Jadwal dihapus");
  };

  const classMap = new Map(classes.map((c) => [c.id, c.name]));
  const teacherMap = new Map(teachers.map((t) => [t.id, t.name]));
  const filtered = rows.filter((r) => filterClass === "all" || r.class_id === filterClass);

  return (
    <div>
      <PageHeader
        title="Jadwal Pelajaran"
        description={`${rows.length} jadwal terdaftar`}
        actions={
          <>
            <Select value={filterClass} onValueChange={setFilterClass}>
              <SelectTrigger className="flex-1 sm:w-44 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua kelas</SelectItem>
                {classes.map((c) => <SelectItem key={c.id} value={c.id}>Kelas {c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button onClick={startNew} className="rounded-xl shrink-0"><Plus className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Tambah Jadwal</span></Button></DialogTrigger>
              <DialogContent className="rounded-3xl">
                <DialogHeader><DialogTitle>{editing ? "Edit Jadwal" : "Tambah Jadwal"}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5"><Label>Hari</Label>
                      <Select value={form.day} onValueChange={(v) => setForm({ ...form, day: v as Day })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{days.map((d) => <SelectItem key={d} value={d}>{d[0].toUpperCase() + d.slice(1)}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5"><Label>Kelas</Label>
                      <Select value={form.class_id || ""} onValueChange={(v) => setForm({ ...form, class_id: v })}>
                        <SelectTrigger><SelectValue placeholder="Pilih kelas" /></SelectTrigger>
                        <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5"><Label>Jam Mulai</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Jam Selesai</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
                  </div>
                  <div className="space-y-1.5"><Label>Mata Pelajaran</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Guru</Label>
                    <Select value={form.teacher_id || ""} onValueChange={(v) => setForm({ ...form, teacher_id: v || null })}>
                      <SelectTrigger><SelectValue placeholder="Pilih guru" /></SelectTrigger>
                      <SelectContent>{teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
                  <Button onClick={save} className="rounded-xl">{editing ? "Simpan" : "Tambah"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        {days.map((day) => {
          const items = filtered.filter((r) => r.day === day).sort((a, b) => a.start_time.localeCompare(b.start_time));
          return (
            <Card key={day} className="p-3 sm:p-5 rounded-2xl">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <h3 className="font-semibold capitalize text-primary text-sm sm:text-base">{day}</h3>
                <span className="text-[10px] sm:text-xs text-muted-foreground">{items.length} jadwal</span>
              </div>
              {items.length === 0 ? (
                <div className="text-xs text-muted-foreground">Tidak ada jadwal</div>
              ) : (
                <div className="space-y-1.5 sm:space-y-2">
                  {items.map((s) => (
                    <div key={s.id} className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl bg-surface-variant hover:bg-accent/50 transition-colors group">
                      <div className="text-[11px] sm:text-xs font-mono text-muted-foreground shrink-0 w-16 sm:w-24 pt-0.5 leading-tight">
                        <div className="flex items-center gap-1"><Clock className="w-3 h-3" />{s.start_time.slice(0,5)}</div>
                        <div className="pl-4">{s.end_time.slice(0,5)}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-xs sm:text-sm truncate">{s.subject}</div>
                        <div className="text-[11px] sm:text-xs text-muted-foreground truncate">
                          {teacherMap.get(s.teacher_id || "") || "-"} · {classMap.get(s.class_id || "") || "-"}
                        </div>
                      </div>
                      <div className="flex sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(s)}><Pencil className="w-3 h-3" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><Trash2 className="w-3 h-3 text-destructive" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Hapus jadwal?</AlertDialogTitle><AlertDialogDescription>Tindakan tidak dapat dibatalkan.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => remove(s.id)}>Hapus</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

    </div>
  );
}
